import React from "react";
import PropTypes from "prop-types";
import { sortSeries, filterSeries } from "./rank";
import { fmtEur, fmtPct, fmtSmape, formatSeriesName } from "./format";
import "../../../../components/ui-components/DataTable.css";

// DriverPill renders a small pill for the dominant driver type.
// volume → primary, price → warning, other → neutral
function DriverPill({ driver }) {
  if (!driver) return <span className="text-text-3">—</span>;
  const d = driver.toLowerCase();
  let cls = "inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ";
  if (d === "volume") {
    cls += "bg-primary-soft text-primary";
  } else if (d === "price") {
    cls += "bg-warning-soft text-warning";
  } else {
    cls += "bg-surface-2 text-text-2";
  }
  const label = d.charAt(0).toUpperCase() + d.slice(1);
  return <span className={cls}>{label}</span>;
}
DriverPill.propTypes = { driver: PropTypes.string };

// SortButton renders a column header that is clickable to toggle sort.
function SortButton({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-0.5 font-medium uppercase tracking-wide text-xs transition-colors ${
        active ? "text-primary" : "text-text-3 hover:text-text-2"
      }`}
    >
      {label}
      {active && (
        <span aria-hidden="true" className="ml-0.5 text-xs">
          ↓
        </span>
      )}
    </button>
  );
}
SortButton.propTypes = {
  label: PropTypes.string,
  active: PropTypes.bool,
  onClick: PropTypes.func,
};

export default function SeriesRankTable({
  rows,
  loading,
  sortKey,
  onSortKey,
  selected,
  onSelect,
  // Support the shell passing data/isLoading instead of rows/loading
  data,
  isLoading,
}) {
  // Accept both prop naming conventions (shell uses data/isLoading; task spec uses rows/loading)
  const effectiveRows = rows ?? data ?? [];
  const effectiveLoading = loading ?? isLoading ?? false;

  // Mirror the actual sort key used below so exactly one header reads as active
  // (a falsy sortKey defaults to "growth", not "both columns active").
  const effectiveSort = sortKey || "growth";
  // Hide empty/unrated series (no spend, no growth, no model) before sorting —
  // they add noise without telling the user anything.
  const sorted = sortSeries(filterSeries(effectiveRows), effectiveSort);

  if (effectiveLoading) {
    return (
      <div className="px-4 py-8 text-center text-sm text-text-3">Loading series…</div>
    );
  }

  if (!sorted || sorted.length === 0) {
    return (
      <div className="px-4 py-12 text-center">
        <p className="text-sm text-text-3">No series available.</p>
        <p className="mt-1 text-xs text-text-3">
          Select a group and horizon to load forecast series.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-card border border-card-bd bg-card">
      <table className="data-table">
        <thead>
          <tr>
            <th>
              Series
            </th>
            <th>
              <SortButton
                label="Projected"
                active={effectiveSort === "spend"}
                onClick={() => onSortKey && onSortKey("spend")}
              />
            </th>
            <th>
              <SortButton
                label="Growth"
                active={effectiveSort === "growth"}
                onClick={() => onSortKey && onSortKey("growth")}
              />
            </th>
            <th>
              Driver
            </th>
            <th>
              Model · sMAPE
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const isSelected = selected === row.name;
            const growthPositive = (row.growth_eur || 0) > 0;

            return (
              <tr
                key={row.name}
                onClick={() => onSelect && onSelect(row.name)}
                className={`cursor-pointer transition-colors hover:bg-surface-2 ${
                  isSelected ? "bg-primary-soft hover:bg-primary-soft" : ""
                }`}
              >
                {/* Series name — "<service>|<unit>" rendered readably, with the
                    unit's meaning in a tooltip. */}
                <td>
                  {(() => {
                    const sn = formatSeriesName(row.name);
                    return (
                      <span
                        className={`inline-flex items-center gap-1.5 font-medium ${
                          isSelected ? "text-primary" : "text-text"
                        }`}
                        {...(sn.hint
                          ? { "data-tooltip-id": "instantTooltip", "data-tooltip-content": sn.hint }
                          : {})}
                      >
                        {sn.label}
                      </span>
                    );
                  })()}
                </td>

                {/* Projected spend */}
                <td className="tabular-nums text-text-2">
                  {fmtEur(row.projected_spend)}
                </td>

                {/* Growth — red/up when positive */}
                <td className="tabular-nums">
                  <div className="flex items-center gap-1.5">
                    {growthPositive && row.growth_eur != null && (
                      <span className="text-xs text-danger" aria-hidden="true">
                        ↑
                      </span>
                    )}
                    <span
                      className={
                        row.growth_eur != null && growthPositive
                          ? "font-medium text-danger"
                          : row.growth_eur != null && row.growth_eur < 0
                          ? "font-medium text-success"
                          : "text-text-3"
                      }
                    >
                      {fmtEur(row.growth_eur)}
                    </span>
                    <span className="text-xs text-text-3">
                      {fmtPct(row.growth_pct)}
                    </span>
                  </div>
                </td>

                {/* Dominant driver pill */}
                <td>
                  <DriverPill driver={row.dominant_driver} />
                </td>

                {/* Model · sMAPE */}
                <td className="text-text-3">
                  {row.algorithm ? (
                    <span>
                      <span className="font-medium text-text-2">{row.algorithm}</span>
                      <span className="mx-1 text-text-3">·</span>
                      <span
                        className={
                          row.smape == null ? "text-text-3 italic" : "tabular-nums"
                        }
                      >
                        {fmtSmape(row.smape)}
                      </span>
                    </span>
                  ) : (
                    <span className="text-text-3 italic">not yet rated</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

SeriesRankTable.propTypes = {
  rows: PropTypes.array,
  data: PropTypes.array,
  loading: PropTypes.bool,
  isLoading: PropTypes.bool,
  sortKey: PropTypes.oneOf(["growth", "spend"]),
  onSortKey: PropTypes.func,
  selected: PropTypes.string,
  onSelect: PropTypes.func,
};
